import { z } from 'zod';
import { descriptionSchema, documentIdSchema, finiteNumber, nameSchema } from './common';

/** 車両の種類。MVP は座席に座る標準型のみ。 */
export const VEHICLE_TYPES = ['sit-down'] as const;

/**
 * 車両の見た目（3D のモデル）。コースターのほか、蒸気機関車・新幹線・リニア・スポーツカー・自転車などを選べる。
 * 物理は、車両の質量・抵抗・限界値で決まり、見た目のモデルは物理に影響しない。
 */
export const VEHICLE_MODELS = [
  'coaster',
  'steam-locomotive',
  'bullet-train',
  'maglev',
  'sports-car',
  'bicycle',
  'mine-cart',
  'bobsled',
  'tram',
] as const;

/** 車両の見た目の種類。 */
export type VehicleModel = (typeof VEHICLE_MODELS)[number];

/** 運転上の限界値（警告表示の閾値。FR-SAF）。G は標準重力加速度で正規化した値。 */
export const vehicleLimitsSchema = z.object({
  /** 最高速度 [m/s] */
  maxSpeed: finiteNumber.min(1).max(200),
  /** 上向きの G の上限（正） */
  maxPositiveG: finiteNumber.min(0.5).max(15),
  /** 下向きの G の下限（負または 0。浮き上がりの許容） */
  maxNegativeG: finiteNumber.min(-10).max(0),
  /** 横 G の上限（正） */
  maxLateralG: finiteNumber.min(0).max(10),
});

/** 車両（編成）の物理パラメータと外観。 */
export const vehicleSchema = z.object({
  id: documentIdSchema,
  name: nameSchema,
  description: descriptionSchema.default(''),
  vehicleType: z.enum(VEHICLE_TYPES),
  /** 1 編成の車両数 */
  carCount: z.number().int().min(1).max(20),
  /** 1 車両あたりの座席数 */
  seatsPerCar: z.number().int().min(1).max(100),
  /** 1 車両の空車質量 [kg] */
  carMass: finiteNumber.min(5).max(100_000),
  /** 乗客 1 人の質量 [kg] */
  passengerMass: finiteNumber.min(30).max(150),
  /** 乗車率（0 = 空車、1 = 満車） */
  occupancy: finiteNumber.min(0).max(1),
  /** 1 車両の長さ [m] */
  carLength: finiteNumber.min(0.5).max(30),
  /** 車両間の間隔 [m] */
  carSpacing: finiteNumber.min(0).max(5),
  /** 幅 [m] */
  width: finiteNumber.min(0.3).max(4.5),
  /** 高さ [m] */
  height: finiteNumber.min(0.5).max(4.5),
  /** 空気抵抗係数 C_d */
  dragCoefficient: finiteNumber.min(0).max(3),
  /** 前面投影面積 A [m²] */
  frontalArea: finiteNumber.min(0.1).max(20),
  /** 転がり抵抗係数 μ_r */
  rollingResistance: finiteNumber.min(0).max(0.2),
  limits: vehicleLimitsSchema,
  appearance: z.object({
    /** 車体の色（`#rrggbb`） */
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/, { message: '#rrggbb の形式で指定してください' }),
    /** 3D のモデル（省略はコースター） */
    model: z.enum(VEHICLE_MODELS).default('coaster'),
  }),
});

/** 車両。 */
export type Vehicle = z.infer<typeof vehicleSchema>;

/**
 * 編成の総質量 [kg] = 車両数 × (車両質量 + 座席数 × 乗客質量 × 乗車率)。
 * 物理計算（空気抵抗の加速度への換算）に使う。
 */
export function totalMass(vehicle: Vehicle): number {
  return (
    vehicle.carCount *
    (vehicle.carMass + vehicle.seatsPerCar * vehicle.passengerMass * vehicle.occupancy)
  );
}

/** 編成の全長 [m] = 車両数 × 車両長 + (車両数 − 1) × 車両間隔。 */
export function trainLength(vehicle: Vehicle): number {
  return vehicle.carCount * vehicle.carLength + (vehicle.carCount - 1) * vehicle.carSpacing;
}

const standardSteel: Vehicle = vehicleSchema.parse({
  id: 'builtin:vehicle/standard-steel',
  name: 'スタンダード（スチール）',
  description: '標準的なスチールコースター 1 編成（6 両・各 4 人乗り）。',
  vehicleType: 'sit-down',
  carCount: 6,
  seatsPerCar: 4,
  carMass: 700,
  passengerMass: 75,
  occupancy: 1,
  carLength: 2.2,
  carSpacing: 0.3,
  width: 1.5,
  height: 1.2,
  dragCoefficient: 1,
  frontalArea: 1.8,
  rollingResistance: 0.01,
  limits: { maxSpeed: 45, maxPositiveG: 5, maxNegativeG: -1.5, maxLateralG: 1.8 },
  appearance: { color: '#d23c3c' },
});

const familyCompact: Vehicle = vehicleSchema.parse({
  id: 'builtin:vehicle/family-compact',
  name: 'ファミリー（コンパクト）',
  description: '小型の 4 両編成（各 2 人乗り）。穏やかなコース向け。',
  vehicleType: 'sit-down',
  carCount: 4,
  seatsPerCar: 2,
  carMass: 400,
  passengerMass: 60,
  occupancy: 1,
  carLength: 1.8,
  carSpacing: 0.25,
  width: 1.1,
  height: 1.1,
  dragCoefficient: 0.9,
  frontalArea: 1.2,
  rollingResistance: 0.012,
  limits: { maxSpeed: 25, maxPositiveG: 3, maxNegativeG: -0.5, maxLateralG: 1 },
  appearance: { color: '#2f8f5b' },
});

/** 組み込みの車両の、共通でない項目を指定して作る（座席に座る型）。 */
function builtin(spec: {
  id: string;
  name: string;
  description: string;
  model: VehicleModel;
  carCount: number;
  seatsPerCar: number;
  carMass: number;
  passengerMass: number;
  occupancy: number;
  carLength: number;
  carSpacing: number;
  width: number;
  height: number;
  dragCoefficient: number;
  frontalArea: number;
  rollingResistance: number;
  limits: z.input<typeof vehicleLimitsSchema>;
  color: string;
}): Vehicle {
  const { model, color, ...rest } = spec;
  return vehicleSchema.parse({
    ...rest,
    vehicleType: 'sit-down',
    appearance: { color, model },
  });
}

const steamLocomotive = builtin({
  id: 'builtin:vehicle/steam-locomotive',
  name: '蒸気機関車（SL）',
  description:
    '機関車・炭水車・客車 2 両の 4 両編成（約 180 t）。重く、空気抵抗は大きいが転がり抵抗は小さい。最高 110 km/h。',
  model: 'steam-locomotive',
  carCount: 4,
  seatsPerCar: 12,
  carMass: 45_000,
  passengerMass: 65,
  occupancy: 0.8,
  carLength: 14,
  carSpacing: 0.8,
  width: 2.9,
  height: 3.9,
  dragCoefficient: 0.9,
  frontalArea: 9,
  rollingResistance: 0.0025,
  limits: { maxSpeed: 31, maxPositiveG: 1.5, maxNegativeG: -0.3, maxLateralG: 0.3 },
  color: '#1f2937',
});

const bulletTrain = builtin({
  id: 'builtin:vehicle/bullet-train',
  name: '新幹線',
  description:
    '流線形の 4 両編成（約 180 t、各 100 席）。空気抵抗が小さく、転がり抵抗も小さい。最高 300 km/h。',
  model: 'bullet-train',
  carCount: 4,
  seatsPerCar: 100,
  carMass: 40_000,
  passengerMass: 65,
  occupancy: 0.7,
  carLength: 25,
  carSpacing: 0.5,
  width: 3.4,
  height: 3.6,
  dragCoefficient: 0.18,
  frontalArea: 11,
  rollingResistance: 0.0015,
  limits: { maxSpeed: 83, maxPositiveG: 1.4, maxNegativeG: -0.4, maxLateralG: 0.15 },
  color: '#f3f6fb',
});

const maglev = builtin({
  id: 'builtin:vehicle/maglev',
  name: 'リニアモーターカー',
  description:
    '浮上走行する 4 両編成（約 100 t、各 40 席）。車輪がないので転がり抵抗はほぼなく、空気抵抗だけが主な抵抗。最高 500 km/h。',
  model: 'maglev',
  carCount: 4,
  seatsPerCar: 40,
  carMass: 25_000,
  passengerMass: 65,
  occupancy: 0.7,
  carLength: 24,
  carSpacing: 0.4,
  width: 2.9,
  height: 3.2,
  dragCoefficient: 0.2,
  frontalArea: 8.5,
  rollingResistance: 0.0005,
  limits: { maxSpeed: 140, maxPositiveG: 1.4, maxNegativeG: -0.4, maxLateralG: 0.12 },
  color: '#8ec9f2',
});

const sportsCar = builtin({
  id: 'builtin:vehicle/sports-car',
  name: 'スポーツカー',
  description:
    '2 人乗りの低い車体（約 1.6 t）。空気抵抗は小さいが、タイヤの転がり抵抗は大きめ。最高 290 km/h。',
  model: 'sports-car',
  carCount: 1,
  seatsPerCar: 2,
  carMass: 1450,
  passengerMass: 75,
  occupancy: 1,
  carLength: 4.5,
  carSpacing: 0,
  width: 1.85,
  height: 1.25,
  dragCoefficient: 0.3,
  frontalArea: 2,
  rollingResistance: 0.012,
  limits: { maxSpeed: 80, maxPositiveG: 3, maxNegativeG: -0.8, maxLateralG: 1.2 },
  color: '#e11d48',
});

const bicycle = builtin({
  id: 'builtin:vehicle/bicycle',
  name: '自転車',
  description:
    '1 人乗り（車体 12 kg + 人 70 kg）。軽く、人の体が空気抵抗の大半。最高 90 km/h（下り）。',
  model: 'bicycle',
  carCount: 1,
  seatsPerCar: 1,
  carMass: 12,
  passengerMass: 70,
  occupancy: 1,
  carLength: 1.7,
  carSpacing: 0,
  width: 0.6,
  height: 1.1,
  dragCoefficient: 1,
  frontalArea: 0.55,
  rollingResistance: 0.005,
  limits: { maxSpeed: 25, maxPositiveG: 2.5, maxNegativeG: -0.3, maxLateralG: 0.5 },
  color: '#2563eb',
});

const mineCart = builtin({
  id: 'builtin:vehicle/mine-cart',
  name: '炭鉱トロッコ',
  description: '2 人乗りの小さな鉄のトロッコ 3 両。古典的なコースター。車輪の転がり抵抗が大きい。',
  model: 'mine-cart',
  carCount: 3,
  seatsPerCar: 2,
  carMass: 350,
  passengerMass: 70,
  occupancy: 1,
  carLength: 1.8,
  carSpacing: 0.3,
  width: 1.1,
  height: 1,
  dragCoefficient: 1.1,
  frontalArea: 1,
  rollingResistance: 0.015,
  limits: { maxSpeed: 25, maxPositiveG: 3.5, maxNegativeG: -0.5, maxLateralG: 1 },
  color: '#92400e',
});

const bobsled = builtin({
  id: 'builtin:vehicle/bobsled',
  name: 'ボブスレー',
  description:
    '4 人乗りのソリ（車体 210 kg + 選手）。氷の上の滑走で、抵抗が小さく、大きな G に耐える。最高 150 km/h。',
  model: 'bobsled',
  carCount: 1,
  seatsPerCar: 4,
  carMass: 210,
  passengerMass: 90,
  occupancy: 1,
  carLength: 3.8,
  carSpacing: 0,
  width: 0.67,
  height: 0.7,
  dragCoefficient: 0.35,
  frontalArea: 0.55,
  rollingResistance: 0.003,
  limits: { maxSpeed: 42, maxPositiveG: 5, maxNegativeG: -0.5, maxLateralG: 1.5 },
  color: '#ef4444',
});

const tram = builtin({
  id: 'builtin:vehicle/tram',
  name: '路面電車',
  description:
    '2 両連接の路面電車（約 40 t、各 30 席）。ゆっくり走り、揺れに弱い立ち客を想定して G の限界は小さい。',
  model: 'tram',
  carCount: 2,
  seatsPerCar: 30,
  carMass: 18_000,
  passengerMass: 65,
  occupancy: 0.6,
  carLength: 14,
  carSpacing: 0.5,
  width: 2.4,
  height: 3.3,
  dragCoefficient: 0.8,
  frontalArea: 7.5,
  rollingResistance: 0.003,
  limits: { maxSpeed: 20, maxPositiveG: 1.3, maxNegativeG: -0.3, maxLateralG: 0.3 },
  color: '#16a34a',
});

const highSpeedLaunch = builtin({
  id: 'builtin:vehicle/high-speed-launch',
  name: '高速ローンチ（スチール）',
  description:
    '空気圧・油圧・リニアモーターで発進する、超高速コースター向けの 4 両編成（各 4 人乗り）。空気抵抗が小さく、最高 250 km/h。',
  model: 'coaster',
  carCount: 4,
  seatsPerCar: 4,
  carMass: 800,
  passengerMass: 75,
  occupancy: 1,
  carLength: 2.4,
  carSpacing: 0.3,
  width: 1.5,
  height: 1.1,
  dragCoefficient: 0.6,
  frontalArea: 1.6,
  rollingResistance: 0.008,
  limits: { maxSpeed: 70, maxPositiveG: 5, maxNegativeG: -1.5, maxLateralG: 1.8 },
  color: '#c81e1e',
});

/** 組み込みの車両（FR-VEH-001）。ID は `builtin:vehicle/...`。先頭が既定の車両。 */
export const BUILTIN_VEHICLES: readonly Vehicle[] = [
  standardSteel,
  familyCompact,
  highSpeedLaunch,
  mineCart,
  steamLocomotive,
  tram,
  bulletTrain,
  maglev,
  sportsCar,
  bicycle,
  bobsled,
];

/** 既定の車両の ID。 */
export const DEFAULT_VEHICLE_ID = standardSteel.id;

/**
 * ID から車両を探す。組み込みを先に、次にプロジェクトのユーザー定義を探す。
 * 見つからなければ `undefined`。
 */
export function findVehicle(
  id: string,
  userVehicles: readonly Vehicle[] = [],
): Vehicle | undefined {
  return BUILTIN_VEHICLES.find((v) => v.id === id) ?? userVehicles.find((v) => v.id === id);
}
